/**
 * Client-Side Merge Utility
 * 
 * Shared logic for downloading video chunks from R2 CDN and merging them
 * client-side using FFmpeg.wasm (concat -c copy, no re-encoding).
 * Used by both ConstancePage and the main editor's useExportManager.
 */

import { FFmpegService } from './ffmpeg-service';

// ============================================================================
// TYPES
// ============================================================================

export interface ChunkStatus {
    index: number;
    mode?: 'local' | 'server'; // 'local' = WebCodecs (Web), 'server' = Cloud
    serverStatus: 'pending' | 'downloading' | 'encoding' | 'complete' | 'failed';
    serverProgress: number;
    url: string | null;
    localStatus: 'waiting' | 'downloading' | 'downloaded' | 'failed';
    localProgress: number;
    size: number; // bytes
    blob?: Blob; // <-- Added to hold local WebCodecs result for R2 upload
}

export type ChunkUpdateCallback = (chunkIndex: number, updates: Partial<ChunkStatus>) => void;

interface MergeChunksOptions {
    reencode?: boolean;
}

export function createInitialChunks(totalChunks: number): ChunkStatus[] {
    return Array.from({ length: totalChunks }, (_, i) => ({
        index: i,
        serverStatus: 'pending' as const,
        serverProgress: 0,
        url: null,
        localStatus: 'waiting' as const,
        localProgress: 0,
        size: 0,
    }));
}

// ============================================================================
// DOWNLOAD CHUNK TO FFMPEG FS
// ============================================================================

/** Track active downloads to avoid duplicates */
const activeDownloads = new Set<number>();

/**
 * Download a single chunk from R2 CDN and write it to FFmpeg's virtual FS.
 * Streams the download and reports progress via callback.
 * 
 * @returns The size in bytes of the downloaded chunk
 */
export async function downloadChunkToFFmpegFS(
    chunkIndex: number,
    chunkUrl: string,
    onUpdate: ChunkUpdateCallback,
): Promise<number> {
    if (activeDownloads.has(chunkIndex)) return 0;
    activeDownloads.add(chunkIndex);

    try {
        onUpdate(chunkIndex, { localStatus: 'downloading', localProgress: 0 });

        const resp = await fetch(chunkUrl);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

        const reader = resp.body?.getReader();
        const contentLength = parseInt(resp.headers.get('content-length') || '0', 10);
        const buffers: Uint8Array[] = [];
        let received = 0;

        if (reader) {
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffers.push(value);
                received += value.length;
                const pct = contentLength > 0 ? Math.round((received / contentLength) * 100) : 0;
                onUpdate(chunkIndex, { localProgress: pct });
            }
        }

        // Combine into single Uint8Array
        const totalLength = buffers.reduce((acc, arr) => acc + arr.length, 0);
        const combined = new Uint8Array(totalLength);
        let offset = 0;
        for (const arr of buffers) {
            combined.set(arr, offset);
            offset += arr.length;
        }

        // Write to FFmpeg virtual FS
        const ffmpeg = await FFmpegService.getInstance();
        const filename = `chunk_${String(chunkIndex).padStart(3, '0')}.mp4`;
        await ffmpeg.writeFile(filename, combined);

        onUpdate(chunkIndex, { localStatus: 'downloaded', localProgress: 100, size: totalLength });
        console.log(`[ClientMerge] Chunk ${chunkIndex} downloaded: ${(totalLength / 1024 / 1024).toFixed(1)} MB`);

        return totalLength;
    } catch (err: any) {
        console.error(`[ClientMerge] Chunk ${chunkIndex} download failed:`, err);
        onUpdate(chunkIndex, { localStatus: 'failed' });
        throw err;
    } finally {
        activeDownloads.delete(chunkIndex);
    }
}

/**
 * Check if a chunk download is already in progress.
 */
export function isChunkDownloading(chunkIndex: number): boolean {
    return activeDownloads.has(chunkIndex);
}

// ============================================================================
// MERGE CHUNKS WITH FFMPEG
// ============================================================================

/**
 * Merge all downloaded chunks using FFmpeg.wasm concat demuxer.
 * Uses `-c copy` so there's no re-encoding — essentially instant.
 * 
 * @returns Blob of the merged MP4 video
 */
export async function mergeChunksWithFFmpeg(totalChunks: number, options: MergeChunksOptions = {}): Promise<Blob> {
    console.log(`[ClientMerge] Starting mergeChunksWithFFmpeg with totalChunks=${totalChunks}, options=`, options);
    const ffmpeg = await FFmpegService.getInstance();
    try {
        // ALWAYS use copy mode for hybrid exports. 
        // Re-encoding in the browser with ffmpeg.wasm crashes the tab due to memory/CPU limits.
        // The server and local chunks are already explicitly matched in bitrate and framerate.
        options.reencode = false;

        if (options.reencode) {
            console.log(`[ClientMerge] Re-encode mode selected. Building input args...`);
            const inputArgs = Array.from({ length: totalChunks }, (_, i) => [
                '-i', `chunk_${String(i).padStart(3, '0')}.mp4`
            ]).flat();
            const concatInputs = Array.from({ length: totalChunks }, (_, i) => `[${i}:v:0][${i}:a:0]`).join('');
            const filterComplex = `${concatInputs}concat=n=${totalChunks}:v=1:a=1[outv][outa]`;

            console.log(`[ClientMerge] Executing FFmpeg re-encode with filter_complex=${filterComplex}...`);
            await ffmpeg.exec([
                ...inputArgs,
                '-filter_complex', filterComplex,
                '-map', '[outv]',
                '-map', '[outa]',
                '-c:v', 'libx264',
                '-preset', 'ultrafast',
                '-crf', '18',
                '-pix_fmt', 'yuv420p',
                '-r', '30',
                '-c:a', 'aac',
                '-b:a', '192k',
                '-ar', '48000',
                '-ac', '2',
                '-movflags', '+faststart',
                '-y', 'merged_output.mp4'
            ]);
            console.log(`[ClientMerge] FFmpeg exec re-encode completed`);
        } else {
            // Create concat list
            console.log(`[ClientMerge] Copy mode selected. Building concat list...`);
            const concatContent = Array.from({ length: totalChunks }, (_, i) =>
                `file 'chunk_${String(i).padStart(3, '0')}.mp4'`
            ).join('\n');
            await ffmpeg.writeFile('concat_chunks.txt', new TextEncoder().encode(concatContent));
            console.log(`[ClientMerge] Wrote concat_chunks.txt`);

            // FFmpeg concat -c copy (instant, no re-encoding)
            console.log(`[ClientMerge] Executing FFmpeg copy concat...`);
            await ffmpeg.exec([
                '-f', 'concat', '-safe', '0',
                '-i', 'concat_chunks.txt',
                '-c', 'copy', '-movflags', '+faststart',
                '-y', 'merged_output.mp4'
            ]);
            console.log(`[ClientMerge] FFmpeg exec copy concat completed`);
        }

        // Read result
        console.log(`[ClientMerge] Reading merged_output.mp4...`);
        const outputData = await ffmpeg.readFile('merged_output.mp4') as Uint8Array;
        console.log(`[ClientMerge] Creating Blob from outputData...`);
        const blob = new Blob([outputData.slice().buffer], { type: 'video/mp4' });

        console.log(`[ClientMerge] Merge complete: ${(blob.size / 1024 / 1024).toFixed(1)} MB`);
        return blob;
    } finally {
        // Cleanup FFmpeg FS
        for (let i = 0; i < totalChunks; i++) {
            await ffmpeg.deleteFile(`chunk_${String(i).padStart(3, '0')}.mp4`).catch(() => { });
        }
        await ffmpeg.deleteFile('concat_chunks.txt').catch(() => { });
        await ffmpeg.deleteFile('merged_output.mp4').catch(() => { });
    }
}

// ============================================================================
// HYBRID OVERLAY POST-PROCESSING
// ============================================================================

import type { Clip, Track, SubtitleStyle } from './types';

interface OverlaySegment {
    clip: Clip;
    type: 'intro' | 'outro' | 'overlay';
}

/**
 * Detect overlay clips that need local rendering.
 * Returns the overlay clips and whether any exist.
 */
export function detectOverlayClips(clips: Clip[], tracks: Track[]): {
    hasOverlays: boolean;
    overlayClips: Clip[];
    totalDuration: number;
} {
    const visibleClips = clips.filter(clip => {
        const track = tracks.find(t => t.id === clip.trackId);
        return track?.isVisible;
    });

    const overlayClips = visibleClips.filter(c =>
        c.clipRole === 'intro' ||
        c.clipRole === 'outro' ||
        c.type === 'text' ||
        c.type === 'image' ||
        c.type === 'zoom' ||
        c.type === 'synthe' ||
        c.type === 'panel'
    );

    const totalDuration = Math.max(...visibleClips.map(c => c.start + c.duration), 0);

    return {
        hasOverlays: overlayClips.length > 0,
        overlayClips,
        totalDuration,
    };
}

/**
 * Add VFX overlays on top of a merged base video (which already has burned subtitles).
 * Uses WebCodecs + Canvas for frame-by-frame rendering of overlay time ranges.
 * Returns a new Blob with overlays composited.
 *
 * @param baseVideoBlob - Merged MP4 from server (with burned subtitles)
 * @param allClips - Full timeline clips
 * @param tracks - Timeline tracks
 * @param onProgress - Progress callback (0-100)
 * @returns New Blob with overlays rendered on top
 */
export async function addOverlaysToMergedVideo(
    baseVideoBlob: Blob,
    allClips: Clip[],
    tracks: Track[],
    activeSubtitleLanguage: string,
    subtitleStyle: SubtitleStyle,
    onProgress: (progress: number, message: string) => void,
): Promise<Blob> {
    // Lazy-import heavy dependencies
    const { FrameRenderer } = await import('./frame-renderer');
    const { WebMWriter } = await import('./webm-writer');
    const { ASPECT_RATIOS } = await import('./webcodecs-exporter');
    const { fetchFile } = await import('@ffmpeg/util');

    onProgress(0, 'Analyse des overlays...');

    const { overlayClips, totalDuration } = detectOverlayClips(allClips, tracks);

    if (overlayClips.length === 0) {
        return baseVideoBlob; // Nothing to do
    }

    // Separate overlay types
    const introClip = overlayClips.find(c => c.clipRole === 'intro');
    const outroClip = overlayClips.find(c => c.clipRole === 'outro');
    const otherOverlays = overlayClips.filter(c =>
        c.clipRole !== 'intro' && c.clipRole !== 'outro'
    );

    // Get video segments (for timeline-to-video-time mapping)
    const visibleClips = allClips.filter(clip => {
        const track = tracks.find(t => t.id === clip.trackId);
        return track?.isVisible;
    });
    const videoClips = visibleClips.filter(c =>
        c.type === 'video' && !c.clipRole && c.url
    ).sort((a, b) => a.start - b.start);

    onProgress(5, 'Chargement vidéo de base...');

    // Load base video as HTMLVideoElement for Canvas drawing
    const baseBlobUrl = URL.createObjectURL(baseVideoBlob);
    const baseVideo = document.createElement('video');
    baseVideo.crossOrigin = 'anonymous';
    baseVideo.muted = true;
    baseVideo.preload = 'auto';

    await new Promise<void>((resolve, reject) => {
        baseVideo.addEventListener('loadedmetadata', () => resolve(), { once: true });
        baseVideo.addEventListener('error', () => reject(new Error('Failed to load base video')), { once: true });
        baseVideo.src = baseBlobUrl;
        baseVideo.load();
    });

    // Wait for enough data to draw
    await new Promise<void>(resolve => {
        if (baseVideo.readyState >= 2) { resolve(); return; }
        baseVideo.addEventListener('canplay', () => resolve(), { once: true });
    });

    onProgress(10, 'Chargement des sources overlay...');

    // Load intro video if present
    let introVideo: HTMLVideoElement | null = null;
    if (introClip?.url) {
        introVideo = document.createElement('video');
        introVideo.crossOrigin = 'anonymous';
        introVideo.muted = true;
        introVideo.preload = 'auto';
        await new Promise<void>((resolve, reject) => {
            introVideo!.addEventListener('loadedmetadata', () => resolve(), { once: true });
            introVideo!.addEventListener('error', () => reject(new Error('Failed to load intro')), { once: true });
            introVideo!.src = introClip.url!;
            introVideo!.load();
        });
    }

    // Load outro video if present
    let outroVideo: HTMLVideoElement | null = null;
    if (outroClip?.url) {
        outroVideo = document.createElement('video');
        outroVideo.crossOrigin = 'anonymous';
        outroVideo.muted = true;
        outroVideo.preload = 'auto';
        await new Promise<void>((resolve, reject) => {
            outroVideo!.addEventListener('loadedmetadata', () => resolve(), { once: true });
            outroVideo!.addEventListener('error', () => reject(new Error('Failed to load outro')), { once: true });
            outroVideo!.src = outroClip.url!;
            outroVideo!.load();
        });
    }

    // Init FrameRenderer for text/image/zoom overlays
    const frameRenderer = new FrameRenderer(1920, 1080);
    await frameRenderer.preloadMedia(otherOverlays);

    onProgress(15, 'Initialisation encodeur...');

    // Set up WebCodecs VideoEncoder + WebMWriter
    const FPS = 30;
    const canvas = new OffscreenCanvas(1920, 1080);
    const ctx = canvas.getContext('2d', { alpha: true, desynchronized: true });
    if (!ctx) throw new Error('Cannot create canvas context');

    const webmWriter = new WebMWriter({
        width: 1920,
        height: 1080,
        fps: FPS,
        hasAudio: false,
        audioSampleRate: 48000,
    });

    const videoEncoder = new VideoEncoder({
        output: (chunk, metadata) => { webmWriter.addVideoChunk(chunk, metadata); },
        error: (error) => { console.error('[HybridOverlay] Encoder error:', error); },
    });

    videoEncoder.configure({
        codec: 'vp09.00.10.08',
        width: 1920,
        height: 1080,
        bitrate: 20_000_000,
        framerate: FPS,
        latencyMode: 'quality',
    });

    // Render frame-by-frame
    const totalFrames = Math.ceil(totalDuration * FPS);
    const frameDuration = 1_000_000 / FPS;

    onProgress(20, `Rendu overlays: 0/${totalFrames} frames...`);

    // Map timeline time → base video time (base is concat of video clips without gaps)
    function timelineToBaseTime(t: number): number {
        let baseTime = 0;
        for (const vc of videoClips) {
            if (t >= vc.start && t < vc.start + vc.duration) {
                return baseTime + (t - vc.start);
            }
            baseTime += vc.duration;
        }
        return baseTime; // After last clip
    }

    for (let frame = 0; frame < totalFrames; frame++) {
        const currentTime = frame / FPS;

        // Seek base video to correct position
        const baseTime = timelineToBaseTime(currentTime);
        if (Math.abs(baseVideo.currentTime - baseTime) > 0.05) {
            baseVideo.currentTime = baseTime;
            await new Promise<void>(resolve => {
                const check = () => {
                    if (baseVideo.readyState >= 2) resolve();
                    else baseVideo.addEventListener('seeked', () => resolve(), { once: true });
                };
                check();
            });
        }

        // 1. Draw base video frame (already has subtitles burned in)
        ctx.clearRect(0, 0, 1920, 1080);
        ctx.drawImage(baseVideo, 0, 0, 1920, 1080);

        // 2. Draw intro overlay (VP9 alpha)
        if (introVideo && introClip &&
            currentTime >= introClip.start &&
            currentTime < introClip.start + introClip.duration) {
            const it = currentTime - introClip.start;
            if (Math.abs(introVideo.currentTime - it) > 0.1) {
                introVideo.currentTime = it;
                await new Promise<void>(resolve => {
                    if (introVideo!.readyState >= 2) resolve();
                    else introVideo!.addEventListener('seeked', () => resolve(), { once: true });
                });
            }
            ctx.drawImage(introVideo, 0, 0, 1920, 1080);
        }

        // 3. Draw outro overlay (VP9 alpha)
        if (outroVideo && outroClip &&
            currentTime >= outroClip.start &&
            currentTime < outroClip.start + outroClip.duration) {
            const ot = currentTime - outroClip.start;
            if (Math.abs(outroVideo.currentTime - ot) > 0.1) {
                outroVideo.currentTime = ot;
                await new Promise<void>(resolve => {
                    if (outroVideo!.readyState >= 2) resolve();
                    else outroVideo!.addEventListener('seeked', () => resolve(), { once: true });
                });
            }
            ctx.drawImage(outroVideo, 0, 0, 1920, 1080);
        }

        // 4. Draw other overlays (text/images/zoom)
        if (otherOverlays.length > 0) {
            await frameRenderer.renderFrame(ctx as any, currentTime, otherOverlays, tracks, '', subtitleStyle, 'fill', false);
        }

        // 5. Encode frame
        const vf = new VideoFrame(canvas, {
            timestamp: frame * frameDuration,
            duration: frameDuration,
        });
        videoEncoder.encode(vf, { keyFrame: frame % (FPS * 2) === 0 });
        vf.close();

        // Progress update every 30 frames
        if (frame % 30 === 0) {
            const pct = 20 + Math.floor((frame / totalFrames) * 60);
            onProgress(pct, `Rendu overlays: ${frame}/${totalFrames} frames`);
        }
    }

    await videoEncoder.flush();
    onProgress(82, 'Finalisation vidéo avec overlays...');

    const videoOnlyBlob = await webmWriter.complete();
    videoEncoder.close();
    frameRenderer.cleanup();

    // Mux audio from base video into overlay video
    onProgress(85, 'Multiplexage audio...');
    const ffmpeg = await FFmpegService.getInstance();

    await ffmpeg.writeFile('overlay_video.webm', await fetchFile(videoOnlyBlob));
    await ffmpeg.writeFile('base_audio.mp4', new Uint8Array(await baseVideoBlob.arrayBuffer()));

    await ffmpeg.exec([
        '-i', 'overlay_video.webm',
        '-i', 'base_audio.mp4',
        '-c:v', 'copy',
        '-c:a', 'copy',
        '-map', '0:v:0',
        '-map', '1:a:0',
        '-shortest',
        '-y', 'hybrid_final.webm',
    ]);

    const finalData = await ffmpeg.readFile('hybrid_final.webm') as Uint8Array;
    const finalBlob = new Blob([finalData.slice().buffer], { type: 'video/webm' });

    // Cleanup
    await ffmpeg.deleteFile('overlay_video.webm').catch(() => { });
    await ffmpeg.deleteFile('base_audio.mp4').catch(() => { });
    await ffmpeg.deleteFile('hybrid_final.webm').catch(() => { });
    URL.revokeObjectURL(baseBlobUrl);

    onProgress(100, 'Export hybride terminé !');
    console.log(`[HybridOverlay] Final video: ${(finalBlob.size / 1024 / 1024).toFixed(1)} MB`);

    return finalBlob;
}
